/**
 * UrbanMap reference tokens — MEASURED on https://urbanmap.myengie.com (2026-10-09).
 *
 * The production app is Vue 3 + Tailwind 4 where the Tailwind palette is aliased
 * to Fluid core tokens (gray-N → --nj-core-color-grey-N, blue-N → --nj-core-color-blue-N).
 * Greys reference the same Fluid variables (identical in v5 and v6). Blues are pinned to the
 * measured Fluid v5 values: the Studio ships Fluid v6, where blue-200/600 resolve differently.
 * Source of truth: docs/urbanmap/reference/home-map.md
 */
export const UMR = {
  font: 'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  /** Production loads Lato from Google Fonts; UMAppShell injects the same stylesheet. */
  fontHref: 'https://fonts.googleapis.com/css?family=Lato:300,400,700,900&display=swap',
  iconFont: '"Material Symbols Outlined"',

  white: 'var(--nj-core-color-grey-0, #ffffff)',
  black: '#000000',
  text: 'var(--nj-core-color-grey-800, #334551)', // rail labels / icons (text-gray-800)
  textMuted: 'var(--nj-core-color-grey-500, #778c9b)', // close icons (text-gray-500)
  textPlaceholder: 'var(--nj-core-color-grey-600, #718492)',
  textDisabled: 'var(--nj-core-color-grey-400, #9eadb8)',
  border: 'var(--nj-core-color-grey-300, #ccd4da)', // panels, toolbar, controls
  borderSubtle: 'var(--nj-core-color-grey-200, #ebeef1)', // separators, panel header
  borderInput: 'var(--nj-core-color-grey-400, #9eadb8)',
  hover: 'var(--nj-core-color-grey-200, #ebeef1)',
  active: 'var(--nj-core-color-grey-300, #ccd4da)',
  railHover: '#e9f0fb', // Fluid v5 blue-200 (measured)
  logoBg: '#e9f0fb', // Fluid v5 blue-200 (measured)
  logoText: '#007bc5', // Fluid v5 blue-600 (measured) — logo, checked checkbox, DSP eye
  tagBg: 'var(--nj-core-color-grey-200, #ebeef1)',
  checkboxBorder: 'var(--nj-core-color-grey-600, #60798b)', // unchecked fluid-checkbox / radio

  // ─── Panels & forms (measured 2026-10 on Géométries/Mesures/Filtres/Sélection/Export/Import/fiche bâtiment) ───
  grey100: 'var(--nj-core-color-grey-100, #f6f8f9)', // Sélection summary card
  textSecondary: 'var(--nj-core-color-grey-600, #60798b)', // text-gray-600: units, captions, address
  textLabel: 'var(--nj-core-color-grey-700, #435c6e)', // text-gray-700: tool labels, group labels
  textStrong: 'var(--nj-core-color-grey-1000, #171d21)', // text-gray-1000: section titles
  dropzoneBorder: 'var(--nj-core-color-grey-500, #778c9b)',
  blue: '#007bc5', // Fluid v5 blue-600: primary button, radio, draw tools
  blueLink: '#005c9a', // Fluid v5 blue-700: links, information badge
  bluePressed: '#e9f0fb', // Fluid v5 blue-200: aria-pressed tool
  blueHover: '#f2f6fc', // ASSUMPTION: Fluid v5 blue-100 (hover, not measured)
  messageInfoBg: '#e9f0fb',
  messageInfoBorder: '#7eaee9',
  badgeInfoBg: 'rgba(38, 104, 207, 0.1)',
  badgeNeutralBg: 'rgba(5, 43, 80, 0.08)', // also secondary button background
  warning: 'var(--nj-core-color-orange-600, #bc5921)', // info icon of a value with low confidence
  tooltipBg: 'var(--nj-core-color-grey-700, #435c6e)',

  /** Map paint colours (dataset colours — hex allowed, same as charts). */
  map: {
    pipe: '#009de9', // urbanmap-pipe-line "line-color"
    pipeWidth: 3,
    pipeWidthSelected: 6,
    picked: '#6ED2B1',
    consumption: ['#ccd4da', '#ef6b26', '#c0d035', '#29a84a'] as const, // ≤300 / ≤600 / ≤1000 / +1000 MWhu
    fallbackLand: '#f2f2f0',
    fallbackWater: '#cad2d3',
    fallbackBorder: '#ffffff',
  },

  /**
   * Tailwind v4 line-heights used by prod (`text-xs` = calc(1 / 0.75)). Unitless on purpose:
   * WebKit lays 12px × 1.333 out as 15px, Chromium as 16px — keep the same value as prod so both engines match.
   */
  lhXs: 'calc(1 / 0.75)',

  size: {
    railWidth: 80,
    railItemHeight: 59,
    railIcon: 24,
    railLabel: 12,
    logo: 24,
    toolbarHeight: 49,
    toolbarPadding: 8,
    inputWidth: 300,
    inputHeight: 32,
    iconButton: 32,
    panelWidth: 300,
    panelHeaderHeight: 45,
    controlButton: 35,
    basemapTile: 100,
    controlsInset: 16,
  },
} as const;

export const UM_MAPBOX_STYLES = [
  { id: 'light', label: 'Clair', style: 'mapbox://styles/mapbox/light-v11' },
  { id: 'streets', label: 'Terrain', style: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'satellite', label: 'Satellite', style: 'mapbox://styles/mapbox/satellite-streets-v12' },
  { id: 'outdoors', label: 'Relief', style: 'mapbox://styles/mapbox/outdoors-v12' },
] as const;

export type UMBasemapId = (typeof UM_MAPBOX_STYLES)[number]['id'];

/** Zoom levels used by the production app (qC enum). */
export const UM_ZOOM = { country: 6, region: 8, department: 10, city: 12, street: 16 } as const;
